import React, { useEffect } from 'react';
import { X, ChevronRight } from 'lucide-react';
import {
  APP_STORE_URL, GOOGLE_PLAY_URL, WINDOWS_DOWNLOAD_URL, WINDOWS8_LEGACY_DOWNLOAD_URL, MACOS_DOWNLOAD_URL,
} from './constants';

export default function DownloadModal({ isOpen, onClose }) {
  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(2,6,23,0.6)', backdropFilter: 'blur(10px)' }}
      onClick={onClose}
    >
      <div
        className="relative bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="h-1 bg-gradient-to-r from-brand-500 via-brand-cyan to-brand-violet" />

        <button
          onClick={onClose}
          aria-label="Cerrar"
          className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-ink-100 hover:bg-ink-200 transition cursor-pointer z-10"
        >
          <X size={16} strokeWidth={2.5} className="text-ink-500" />
        </button>

        <div className="px-7 pt-7 pb-8">
          <div className="mb-6">
            <p className="text-[11px] font-semibold text-brand-600 uppercase tracking-widest mb-1">Descarga gratuita</p>
            <h2 className="text-xl font-bold text-ink-950 leading-snug">Elige tu plataforma</h2>
            <p className="text-[13px] text-ink-500 mt-1">Disponible en iOS, Android, Windows y macOS.</p>
          </div>

          <div className="space-y-3">
            <PlatformButton
              variant="dark"
              href={APP_STORE_URL}
              onClose={onClose}
              icon={<AppleIcon />}
              eyebrow="Disponible en"
              title="App Store"
              sub="iPhone · iPad"
            />
            <PlatformButton
              variant="light"
              href={GOOGLE_PLAY_URL}
              onClose={onClose}
              icon={<GooglePlayIcon />}
              eyebrow="Disponible en"
              title="Google Play"
              sub="Android"
            />
            <PlatformButton
              variant="windows"
              href={WINDOWS_DOWNLOAD_URL}
              onClose={onClose}
              icon={<WindowsIcon />}
              eyebrow="Descargar para"
              title="Windows"
              sub="Instalador de escritorio"
            />
            <div className="pl-2 -mt-1">
              <a
                href={WINDOWS8_LEGACY_DOWNLOAD_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={onClose}
                className="text-[10px] text-ink-400 hover:text-ink-600 transition"
              >
                ¿Usas Windows 8? Versión legacy
              </a>
            </div>
            <PlatformButton
              variant="mac"
              href={MACOS_DOWNLOAD_URL}
              onClose={onClose}
              icon={<AppleIcon />}
              eyebrow="Descargar para"
              title="macOS"
              sub="Universal (Intel + Apple Silicon)"
            />
          </div>

          <p className="text-center text-[11px] text-ink-400 mt-5">Gratis · Sin tarjeta de crédito</p>
        </div>
      </div>
    </div>
  );
}

function PlatformButton({ variant, href, onClose, icon, eyebrow, title, sub }) {
  const styles = {
    dark:    'bg-ink-950 hover:bg-ink-900 text-white',
    light:   'bg-ink-50  hover:bg-ink-100 border border-ink-200 text-ink-950',
    windows: 'bg-[#0078D4] hover:bg-[#006CBE] text-white',
    mac:     'bg-gradient-to-r from-ink-800 to-ink-700 hover:from-ink-700 hover:to-ink-600 text-white',
  };
  const eyebrowColor = variant === 'light' ? 'text-ink-400' : 'text-white/60';
  const subColor     = variant === 'light' ? 'text-ink-500' : 'text-white/55';

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={onClose}
      className={`flex items-center gap-4 w-full ${styles[variant]} px-5 py-4 rounded-2xl transition-all cursor-pointer group`}
    >
      <div className="w-10 h-10 flex items-center justify-center flex-shrink-0">{icon}</div>
      <div className="flex-1 text-left">
        <div className={`text-[10px] uppercase tracking-wider leading-none mb-0.5 ${eyebrowColor}`}>{eyebrow}</div>
        <div className="text-base font-semibold leading-tight">{title}</div>
        <div className={`text-xs mt-0.5 ${subColor}`}>{sub}</div>
      </div>
      <ChevronRight size={16} className={`flex-shrink-0 ${variant === 'light' ? 'text-ink-300 group-hover:text-ink-500' : 'text-white/40 group-hover:text-white/70'} transition-colors`} />
    </a>
  );
}

function AppleIcon() {
  return (
    <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
    </svg>
  );
}
function GooglePlayIcon() {
  return (
    <svg className="w-7 h-7" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M3.18 23.76c.3.17.65.19.97.07l12.4-7.17-2.89-2.89-10.48 9.99z" fill="#EA4335" />
      <path d="M21.54 10.33l-2.75-1.59-3.22 3.04 3.22 3.22 2.76-1.6c.79-.45.79-1.62-.01-2.07z" fill="#FBBC04" />
      <path d="M2.21.57C1.9.71 1.68 1.02 1.68 1.42v21.16c0 .4.22.71.53.85l11.73-11.14L2.21.57z" fill="#4285F4" />
      <path d="M4.15.24L16.55 7.4l-2.89 2.89L3.18.54C3.5.42 3.85.45 4.15.62v-.38z" fill="#34A853" />
    </svg>
  );
}
function WindowsIcon() {
  return (
    <svg className="w-7 h-7" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M0 3.449L9.75 2.1v9.451H0m10.949-9.602L24 0v11.4H10.949M0 12.6h9.75v9.451L0 20.699M10.949 12.6H24V24l-12.9-1.801" />
    </svg>
  );
}
